import {
  Coupon,
  PlanLineItem,
  PlanLineItemCategoryEnum,
  ShopifyDiscountAffectsClass,
  ShopifyDiscountInfo,
  SubscriptionShopifyCoupon
} from '@petplate/schema';
import sdk, { iterDump } from '@petplate/sdk';
import { cache } from '@petplate/utils/cache';
import difference from 'lodash/difference';
import { GraphqlExecutionError } from '../ui/types/errors';

export const COUPON_SETTINGS_KEYS = {
  SITE_WIDE_COUPON: 'site_wide_coupon',
  SITE_WIDE_SECOND_SUBSCRIPTION_COUPON: 'site_wide_second_subscription_coupon',
  SUB_CANCELLED_ENTREES_DISCOUNT: 'sub_cancelled_entrees_discount',
  HIGH_LTV_DISCOUNT: 'high_ltv_coupon',
  EARLY_CANCEL_COUPON: 'early_cancel_coupon',
  TREATS_COUPON: 'treats_coupon'
};

export const buildCouponUpdateErrorMessage = (err: GraphqlExecutionError) => {
  // The non stackable error is a custom one that we throw in the backend when the coupons are not stackable.
  const couponsNotStackableError = err.response.errors.find(
    (e) => e?.extensions?.code === 'NON_STACKABLE_COUPONS'
  );

  const usageLimitReachedError = err.response.errors.find((e) =>
    e.message?.match(/CUSTOMER_USAGE_LIMIT_REACHED/)
  );

  const defaultError =
    'Coupon(s) could not be applied to your subscription. Contact the support team.';

  if (couponsNotStackableError) {
    // This error message contains the actual coupons that couldn't be stacked, so use the backend message in this case
    // instead of trying to see which coupons were not stackable manually.
    return err.response.errors[0]?.message || defaultError;
  }

  if (usageLimitReachedError) {
    return "You've already used this coupon";
  }

  return defaultError;
};

export const loadCoupons = (limit: number) =>
  cache(
    Object.assign(
      async (a: AbortSignal, title: string) => {
        const coupons = await iterDump(
          a,
          async (cursor) => (await sdk.AdminSearchCoupons({ title, cursor })).shopifyCoupons,
          limit
        );

        return Object.fromEntries(coupons.map(({ code, title }) => [code, title ?? code]));
      },
      { many: true }
    )
  );

const itemCategoryToCollectionName = (item: PlanLineItem) => {
  switch (item.category) {
    case PlanLineItemCategoryEnum.Dry:
    case PlanLineItemCategoryEnum.Wet:
    case PlanLineItemCategoryEnum.Entree:
      return 'meals';
    case PlanLineItemCategoryEnum.Plan:
      return 'plan';
    case PlanLineItemCategoryEnum.SoftTreats:
    case PlanLineItemCategoryEnum.SupplementCookies:
      return 'addons';
    default:
      return '';
  }
};

export const appliesToItem = (
  item: PlanLineItem,
  coupon?: SubscriptionShopifyCoupon | Partial<Coupon>
) => {
  if (coupon?.affectsClass === ShopifyDiscountAffectsClass.DiscountProducts)
    return coupon?.affectedProductSkus?.includes(item.sku || '');
  if (coupon?.affectsClass === ShopifyDiscountAffectsClass.DiscountCollections)
    return coupon?.affectedCollections?.includes(itemCategoryToCollectionName(item));
  if (coupon?.affectsClass === ShopifyDiscountAffectsClass.AllDiscountItems) return true;

  return false;
};

export const calculatePrice = ({
  items,
  discount
}: {
  items: PlanLineItem[];
  discount?: Partial<ShopifyDiscountInfo>;
}) => {
  return items.reduce((coll, item) => {
    if (discount && appliesToItem(item, discount as Partial<Coupon>)) {
      const percent =
        (discount.percentage || 0) > 1
          ? (discount.percentage || 0) / 100
          : discount.percentage || 0;
      const percentOff = 1 - (percent || 0);
      const discountedAmount = Math.max(
        (item.price || 0) * (item?.quantity || 0) - (discount.amount || 0),
        0
      );

      return coll + discountedAmount * percentOff;
    }

    return coll + (item.price || 0) * (item.quantity || 0);
  }, 0);
};

/**
 *
 * @param coupon The coupon applied on the subscription
 * @param itemSku The item sku that is being added to the subscription
 * @param itemType The type of the item
 * @returns true if this item can still be discounted by the coupon.
 */
export const willItemBeDiscounted = (coupon: SubscriptionShopifyCoupon, item: PlanLineItem) => {
  if (coupon.appliesToAllItems) {
    return true;
  }

  if (coupon.entitledProducts?.find((p) => p.sku === item.sku)) {
    return true;
  }

  if (coupon.limited) {
    return false; // We cannot re-apply the coupon to discount the new item
  }

  if (appliesToItem(item, coupon)) {
    return true; // The coupon can be reapplied to the new item
  }

  return false;
};

/**
 *
 * @param coupon The coupon applied on the subscription
 * @param addedItems the list of item skus added to the subscription
 * @param removedItems the list of item skus removed from the subscription
 * @returns true if the coupon will be removed from the subscription by the system with the new items state.
 *
 * The system removes the coupon if it detects that it cannot re-apply the coupon after changing the items.
 */
export const willCouponBeRemoved = (
  coupon: SubscriptionShopifyCoupon,
  addedItems: PlanLineItem[],
  removedItems: PlanLineItem[]
) => {
  // Order discounts apply to all items. Even if the order discount is limitted and the customer completely switches meal
  // plan and selects other items, the discount will still be applied.
  if (coupon.appliesToAllItems) {
    return false;
  }

  const entitledProductSkus = coupon.entitledProducts?.map((p) => p.sku ?? '') || [];

  // Either the coupon applies to all items, or already applies to the new items, or the coupon definition
  // contains the new added items.
  const appliesToAnyNewItem = addedItems.some(
    (item) => entitledProductSkus.includes(item.sku || '') || appliesToItem(item, coupon)
  );

  const willHaveNoMoreEntitledLines =
    difference(
      entitledProductSkus,
      removedItems.map((item) => item.sku || '')
    ).length === 0;

  // We cannot re-apply a limited coupon (so no need to check the new added items) and this coupon will be left with
  // no entitled lines, so we need to remove it.
  if (coupon.limited && willHaveNoMoreEntitledLines) {
    return true;
  }

  // If the coupon is not limited and it doesn't apply to any new item or will be left with no entitled items, remove it.
  if (!coupon.limited && !appliesToAnyNewItem && willHaveNoMoreEntitledLines) {
    return true;
  }

  return false;
};
